/* global __DEV__ */
import { isDesktop, isMac, isWindows, isLinux } from '@trello/browser';
import { analyticsWebClientEnv, clientVersion, locale } from '@trello/config';
import { isValidAction } from './constants/Action';
import { isValidActionSubject } from './constants/ActionSubject';
import { isValidActionSubjectId } from './constants/ActionSubjectId';
import { isValidSource } from './constants/Source';
import AnalyticsWebClient, {
  ApdexStartEvent,
  ApdexStopEvent,
  TaskSessionManager,
  ScreenEvent,
  UIEvent,
  TrackEvent,
  OperationalEvent,
  envType,
  originType,
  platformType,
  AnalyticsWebclientConfig,
} from '@atlassiansox/analytics-web-client';

type EnvironmentVariableType = 'DEV' | 'STAGING' | 'PROD';

const config = {
  env: envType[analyticsWebClientEnv as EnvironmentVariableType],
  product: 'trello',
  version: clientVersion,
  origin: isDesktop() ? originType.DESKTOP : originType.WEB,
  platform: null,
  locale,
} as AnalyticsWebclientConfig;

if (isDesktop()) {
  switch (true) {
    case isMac():
      config.platform = platformType.MAC;
      break;
    case isWindows():
      config.platform = platformType.WINDOWS;
      break;
    case isLinux():
      config.platform = platformType.LINUX;
      break;
    default:
  }
}

const validateParams = (e: UIEvent | TrackEvent | OperationalEvent) => {
  if (!isValidAction(e.action) && __DEV__) {
    console.error(
      `'${e.action}' is not a valid Action (see packages/atlassian-analytics/src/constants/Action.ts)`,
    );
  }
  if (!isValidActionSubject(e.actionSubject) && __DEV__) {
    console.error(
      `'${e.actionSubject}' is not a valid ActionSubject (see packages/atlassian-analytics/src/constants/ActionSubject.ts)`,
    );
  }
  if (
    e.actionSubjectId &&
    !isValidActionSubjectId(e.actionSubjectId) &&
    __DEV__
  ) {
    console.error(
      `'${e.actionSubjectId}' is not a valid ActionSubjectId (see packages/atlassian-analytics/src/constants/ActionSubjectId.ts)`,
    );
  }
  if (!isValidSource(e.source) && __DEV__) {
    console.error(
      `'${e.source}' is not a valid Source (see packages/atlassian-analytics/src/constants/Source.ts)`,
    );
  }
};

class AnalyticsWrapper implements AnalyticsWebClient {
  analytics: AnalyticsWebClient;

  constructor(clientConfig: AnalyticsWebclientConfig) {
    this.analytics = new AnalyticsWebClient(clientConfig);
  }

  sendScreenEvent(e: ScreenEvent) {
    if (!isValidSource(e.name) && __DEV__) {
      console.error(
        `'${e.name}' is not a valid Source (see packages/atlassian-analytics/src/constants/Source.ts)`,
      );
    }
    this.analytics.sendScreenEvent(e);
  }

  sendUIEvent(e: UIEvent) {
    validateParams(e);
    this.analytics.sendUIEvent(e);
  }

  sendTrackEvent(e: TrackEvent) {
    validateParams(e);
    this.analytics.sendTrackEvent(e);
  }

  sendOperationalEvent(e: OperationalEvent) {
    validateParams(e);
    this.analytics.sendOperationalEvent(e);
  }

  setTenantInfo(
    tenantType: 'cloudId' | 'orgId' | 'none' | 'opsgenieCustomerId',
    tenantId?: string,
  ) {
    this.analytics.setTenantInfo(tenantType, tenantId);
  }

  setOrgInfo(orgId: string) {
    this.analytics.setOrgInfo(orgId);
  }

  setUserInfo(
    userType: 'atlassianAccount' | 'hashedEmail' | 'trello' | 'opsgenie',
    userId: string,
  ) {
    this.analytics.setUserInfo(userType, userId);
  }

  sendIdentifyEvent(
    userType: 'atlassianAccount' | 'hashedEmail' | 'trello' | 'opsgenie',
    userId: string,
  ) {
    this.analytics.sendIdentifyEvent(userType, userId);
  }

  startUIViewedEvent() {
    this.analytics.startUIViewedEvent();
  }

  stopUIViewedEvent() {
    this.analytics.stopUIViewedEvent();
  }

  // Apdex
  startApdexEvent(e: ApdexStartEvent) {
    this.analytics.startApdexEvent(e);
  }

  stopApdexEvent(e: ApdexStopEvent) {
    this.analytics.stopApdexEvent(e);
  }

  getApdexStart(e: ApdexStartEvent) {
    this.analytics.getApdexStart(e);
  }

  /**
   * Some child components (e.g. @atlaskit/atlassian-switcher) require consumers
   * to fire their events. In these cases, we don't want to do any validation.
   *
   * This function is appropriate inspired by React's dangerouslySetInnerHTML as it
   * bypasses in-built allow-lists. Use with care!
   */
  dangerouslyGetAnalyticsWebClient(): AnalyticsWebClient {
    return this.analytics;
  }

  // Task Sessions
  readonly task: TaskSessionManager;
}

export const Analytics = new AnalyticsWrapper(config);
